
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  fetchOpportunities, 
  fetchOpportunitiesCount,
  createOpportunity, 
  updateOpportunity,
  fetchOpportunityById,
  fetchOpportunityNotes,
  createOpportunityNote,
  fetchOpportunityActivities,
  createOpportunityActivity,
  deleteOpportunity,
  fetchFilteredOpportunities
} from "@/services";
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from "@/hooks/use-toast";

export function useOpportunities(options?: { limit?: number; offset?: number }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { session } = useAuth();
  
  const opportunitiesQuery = useQuery({
    queryKey: ['opportunities', options],
    queryFn: () => fetchOpportunities(options),
    enabled: !!session,
  });
  
  const opportunitiesCountQuery = useQuery({
    queryKey: ['opportunitiesCount'],
    queryFn: fetchOpportunitiesCount,
    enabled: !!session,
  });

  const createOpportunityMutation = useMutation({
    mutationFn: createOpportunity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      toast({
        title: "Opportunity created",
        description: "The opportunity has been successfully created.",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to create opportunity",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const updateOpportunityMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => updateOpportunity(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      toast({
        title: "Opportunity updated",
        description: "The opportunity has been successfully updated.",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to update opportunity",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const deleteOpportunityMutation = useMutation({
    mutationFn: deleteOpportunity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      toast({
        title: "Opportunity deleted",
        description: "The opportunity has been successfully deleted.",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to delete opportunity",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const getOpportunityQuery = (id: string) => useQuery({
    queryKey: ['opportunities', id],
    queryFn: () => fetchOpportunityById(id),
    enabled: !!id
  });

  // Notes related queries and mutations
  const getOpportunityNotesQuery = (opportunityId: string) => useQuery({
    queryKey: ['opportunityNotes', opportunityId],
    queryFn: () => fetchOpportunityNotes(opportunityId),
    enabled: !!opportunityId
  });

  const createOpportunityNoteMutation = useMutation({
    mutationFn: createOpportunityNote,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['opportunityNotes', variables.opportunity_id] });
      toast({
        title: "Note added",
        description: "Your note has been added to this opportunity.",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to add note",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  // Activities related queries and mutations
  const getOpportunityActivitiesQuery = (opportunityId: string) => useQuery({
    queryKey: ['opportunityActivities', opportunityId],
    queryFn: () => fetchOpportunityActivities(opportunityId),
    enabled: !!opportunityId
  });

  const createOpportunityActivityMutation = useMutation({
    mutationFn: createOpportunityActivity,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['opportunityActivities', variables.opportunity_id] 
      });
    },
    onError: (error) => {
      }
  });

  // Filtered opportunities function
  const getFilteredOpportunitiesQuery = async (filters: {
    stage?: string;
    search?: string;
    orgId?: string;
    ownerId?: string;
    minValue?: number;
    maxValue?: number;
    startDate?: string;
    endDate?: string;
  }) => {
    return await fetchFilteredOpportunities(filters);
  };

  return {
    opportunities: opportunitiesQuery.data || [],
    opportunitiesCount: opportunitiesCountQuery.data || 0,
    isLoading: opportunitiesQuery.isLoading,
    isLoadingCount: opportunitiesCountQuery.isLoading,
    error: opportunitiesQuery.error,
    getOpportunityById: getOpportunityQuery,
    createOpportunity: createOpportunityMutation.mutate,
    updateOpportunity: updateOpportunityMutation.mutate,
    deleteOpportunity: deleteOpportunityMutation.mutate,
    getOpportunityNotes: getOpportunityNotesQuery,
    addOpportunityNote: createOpportunityNoteMutation.mutate,
    getOpportunityActivities: getOpportunityActivitiesQuery,
    logOpportunityActivity: createOpportunityActivityMutation.mutate,
    getFilteredOpportunities: getFilteredOpportunitiesQuery
  };
}
